import { useQuery } from "@tanstack/react-query";
import { TargetIcon, WarningIcon } from "@phosphor-icons/react";
import { fetchDeployerStatsBatch } from "../lib/api";

// Batched deployer hit-rate stats — one request per page, cached 5 min.
export const useDeployerStatsBatch = (creators) => {
  const list = [...new Set((creators || []).filter(Boolean))].sort().slice(0, 60);
  const q = useQuery({
    queryKey: ["deployerStatsBatch", list.join(",")],
    queryFn: () => fetchDeployerStatsBatch(list),
    enabled: list.length > 0,
    staleTime: 300000,
  });
  return q.data?.stats || {};
};

// 🎯 compact hit-rate pill: green for proven runner-makers, red for serial no-runner spammers.
// Renders nothing when there's no meaningful track record (young/unknown devs stay quiet).
export const HitRateBadge = ({ stats, compact = false }) => {
  if (!stats || !stats.totalTokens) return null;
  const proven = (stats.runnerCount || 0) > 0;
  const spammy = !proven && stats.totalTokens >= 10;
  if (!proven && !spammy) return null;
  const hit = Math.round((stats.successRate || 0) * 100);
  const pad = compact ? "text-[9px] px-1 py-0.5" : "text-[10px] px-1.5 py-0.5";
  if (proven) {
    return (
      <span
        data-testid="hit-rate-badge"
        title={`Deployer track record: ${stats.runnerCount} × $1M+ runners${stats.strongRunnerCount ? `, ${stats.strongRunnerCount} × $5M+` : ""} out of ${stats.totalTokens} coins (${hit}% hit rate)${stats.rugCount ? ` · ${stats.rugCount} tagged rugs` : ""}`}
        className={`inline-flex items-center gap-1 font-mono font-bold whitespace-nowrap bg-neon/10 text-neon border border-neon/40 ${pad}`}
      >
        <TargetIcon size={compact ? 10 : 12} weight="bold" />
        {hit}% · {stats.runnerCount}×$1M+{stats.strongRunnerCount > 0 ? ` · ${stats.strongRunnerCount}×$5M+` : ""}
        {stats.rugCount > 0 && <span className="text-[#FF3366]">💀{stats.rugCount}</span>}
      </span>
    );
  }
  return (
    <span
      data-testid="hit-rate-badge-spam"
      title={`${stats.totalTokens} coins deployed, zero ever reached $1M — serial deployer with no runners`}
      className={`inline-flex items-center gap-1 font-mono font-bold whitespace-nowrap bg-[#FF3366]/10 text-[#FF3366] border border-[#FF3366]/40 ${pad}`}
    >
      <WarningIcon size={compact ? 10 : 12} weight="bold" />
      0% · {stats.totalTokens} coins
    </span>
  );
};
